import { useEffect, useState, type RefObject } from "react";

const HIDE_AFTER = 120;

const REVEAL_ZONE = 90;

const THRESHOLD = 6;

export function useHeaderVisibility(footerRef?: RefObject<HTMLElement | null>) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let lastY = window.scrollY;
    let atFooter = false;
    let pointerNearTop = false;

    const onScroll = () => {
      const y = window.scrollY;

      if (atFooter) {
        setVisible(false);
      } else if (pointerNearTop || y <= HIDE_AFTER) {
        setVisible(true);
      } else if (y > lastY + THRESHOLD) {
        setVisible(false);
      } else if (y < lastY - THRESHOLD) {
        setVisible(true);
      }

      if (Math.abs(y - lastY) > THRESHOLD) lastY = y;
    };

    const onPointerMove = (event: PointerEvent) => {
      const near = event.clientY <= REVEAL_ZONE;
      if (near === pointerNearTop) return;

      pointerNearTop = near;
      if (near) {
        if (!atFooter) setVisible(true);
      } else if (window.scrollY > HIDE_AFTER) {
        setVisible(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });

    let observer: IntersectionObserver | undefined;
    const footer = footerRef?.current;
    if (footer) {
      observer = new IntersectionObserver(
        ([entry]) => {
          atFooter = entry.isIntersecting;
          if (atFooter) setVisible(false);
        },
        { threshold: 0.1 },
      );
      observer.observe(footer);
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onPointerMove);
      observer?.disconnect();
    };
  }, [footerRef]);

  return visible;
}
